/**
 * The map's tool bar: a ribbon across the TOP of the map, with its toggle up by
 * the header controls.
 *
 *   ┌──────┬───────┬──────────┬─────────┐
 *   │ All  │ Query │ Navigate │ Measure │ …          [🔧 Tools / Hide]
 *   ├──────┴───────┴──────────┴─────────┴─────────────────────────────
 *   │  🔍 Search   🔖 Bookmark   ✏ Draw   📏 Measure   ➕ Add tool
 *
 * The sections are TABS, the way VertiGIS and the desktop ribbons do it:
 * clicking a section shows only that section's tools. `All` is the default and
 * lists everything, so a small map never makes anyone hunt through tabs.
 *
 * Sections come from the registry (`TOOL_CATEGORIES`) and are exactly the ones
 * the Tool Library groups by, so a tool sits under the same heading on the map
 * as it did in the gallery it was picked from. A new category needs no change
 * here; a tab only appears once a tool in that category is on the map.
 *
 * Two exports rather than one component, because the two halves live in
 * different parts of the map: the ribbon belongs at the top edge where it can
 * run full width, and the toggle belongs with the other map controls. Both are
 * controlled by the parent, so there is a single source of truth for "are the
 * tools showing".
 *
 * Clicking an icon calls `onOpenTool`; the existing tool panels do the work, so
 * the tools themselves are untouched by this layout.
 */

import { useState } from 'react';
import { Plus, Settings2, Wrench, X } from 'lucide-react';
import Tooltip from '../glass/Tooltip';
import { getTool, TOOL_CATEGORIES, type ToolDefinition } from '../../tools/toolRegistry';

/** The always-present first tab. Not a category — it means "no filter". */
const ALL_TAB = 'All';

interface StripProps {
  /** Tool ids enabled on this map, in the order the admin added them. */
  toolIds: string[];
  /** Which tool's panel is open, so its icon reads as active. */
  activeToolId?: string | null;
  onOpenTool: (toolId: string) => void;
  /** Builder only: opens the Tool Library from the end of the ribbon. */
  onAddTool?: () => void;
  /**
   * Builder only: reopens ONE tool's settings.
   *
   * Lives on the tool's own button rather than inside the Tool Library,
   * because that is where someone is when they decide a tool is set up wrong —
   * looking at it on the ribbon, not browsing a gallery of everything. Omitted
   * in the viewer, so a published app cannot be reconfigured by its readers.
   */
  onEditTool?: (toolId: string) => void;
  open: boolean;
}

export function ToolStrip({
  toolIds,
  activeToolId,
  onOpenTool,
  onAddTool,
  onEditTool,
  open,
}: StripProps) {
  const [activeTab, setActiveTab] = useState<string>(ALL_TAB);

  const tools = toolIds
    .map((id) => getTool(id))
    .filter((tool): tool is ToolDefinition => Boolean(tool));

  // Only sections that actually have a tool on this map earn a tab.
  const sections = TOOL_CATEGORIES.filter((category) =>
    tools.some((tool) => tool.category === category),
  );
  const tabs = [ALL_TAB, ...sections];

  /* Removing the last tool of a section removes its tab; fall back to `All`
     rather than leaving the ribbon pointed at a tab that is gone. */
  const currentTab = tabs.includes(activeTab) ? activeTab : ALL_TAB;

  // Nothing to show, and no way to add one: take up no room at all.
  if (!open || (tools.length === 0 && !onAddTool)) return null;

  const visibleTools =
    currentTab === ALL_TAB ? tools : tools.filter((tool) => tool.category === currentTab);

  return (
    <div className="flex-shrink-0 border-b border-slate-200 bg-white">
      {/* Section tabs. Same shape as a desktop ribbon: the active tab reads as
          the front one, the inactive ones sit back on a tinted ground.

          `overflow-y-hidden` is not decorative. `overflow-x-auto` alone leaves
          overflow-y computed as `auto`, so a single stray pixel of vertical
          overflow makes Windows draw a native scrollbar — arrow buttons and
          all — in the corner of the ribbon. */}
      <div
        role="tablist"
        aria-label="Tool sections"
        className="flex items-end gap-1 overflow-x-auto overflow-y-hidden border-b border-slate-200 px-1.5 pt-1"
      >
        {tabs.map((tab) => {
          const active = tab === currentTab;
          return (
            <button
              key={tab}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setActiveTab(tab)}
              className={`flex-shrink-0 rounded-t-md border border-b-0 px-3 py-1.5 text-[11px] font-semibold transition ${
                active
                  ? 'border-slate-200 bg-white text-blue-700 shadow-[inset_0_2px_0_0_#2563eb]'
                  : 'border-transparent bg-slate-50 text-slate-500 hover:bg-slate-100 hover:text-slate-700'
              }`}
            >
              {tab}
            </button>
          );
        })}
      </div>

      <div
        role="toolbar"
        aria-label={currentTab === ALL_TAB ? 'Map tools' : `${currentTab} tools`}
        className="flex items-start gap-0.5 overflow-x-auto overflow-y-hidden px-1.5 py-1"
      >
        {tools.length === 0 && (
          <span className="px-2 py-2 text-[11px] text-slate-400">No tools added yet</span>
        )}

        {visibleTools.map((tool) => {
          const Icon = tool.icon;
          const active = activeToolId === tool.id;
          return (
            <Tooltip key={tool.id} content={tool.description}>
              {/* A DIV wrapper because the gear is a second button, and a
                  button cannot legally contain another one. */}
              <div className="group relative w-[54px] flex-shrink-0">
                <button
                  type="button"
                  onClick={() => onOpenTool(tool.id)}
                  aria-pressed={active}
                  className={`flex w-full flex-col items-center gap-0.5 rounded-md px-0.5 py-1 transition ${
                    active ? 'bg-blue-50' : 'hover:bg-slate-50'
                  }`}
                >
                  <span
                    className={`flex h-6 w-6 items-center justify-center rounded-full border transition ${
                      active
                        ? 'border-blue-600 bg-blue-600 text-white'
                        : 'border-blue-500 bg-white text-blue-600'
                    }`}
                  >
                    <Icon className="h-3 w-3" />
                  </span>
                  <span
                    className={`w-full truncate text-center text-[9.5px] font-medium leading-tight ${
                      active ? 'text-blue-700' : 'text-slate-600'
                    }`}
                  >
                    {tool.name}
                  </span>
                </button>

                {/* Corner gear. Hidden until the tile is hovered or something
                    inside it has focus, so a ribbon of twelve tools is not a
                    ribbon of twelve gears — but it never hides from the
                    keyboard, which is why focus-within counts too. */}
                {onEditTool && (
                  <button
                    type="button"
                    onClick={() => onEditTool(tool.id)}
                    title={`${tool.name} settings`}
                    aria-label={`${tool.name} settings`}
                    className="absolute right-0 top-0 flex h-[15px] w-[15px] items-center justify-center rounded-full border border-slate-300 bg-white text-slate-500 opacity-0 shadow-sm transition hover:border-blue-500 hover:text-blue-600 focus:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-300 group-hover:opacity-100 group-focus-within:opacity-100"
                  >
                    <Settings2 className="h-2.5 w-2.5" />
                  </button>
                )}
              </div>
            </Tooltip>
          );
        })}

        {/* Always reachable, whichever tab is showing — the Library is where a
            tool is added FROM, not something that belongs to one section. */}
        {onAddTool && (
          <Tooltip content="Browse every tool, then set the chosen one up for this map.">
            <button
              type="button"
              onClick={onAddTool}
              className="flex w-[54px] flex-shrink-0 flex-col items-center gap-0.5 rounded-md px-0.5 py-1 transition hover:bg-slate-50"
            >
              <span className="flex h-6 w-6 items-center justify-center rounded-full border border-dashed border-blue-400 bg-white text-blue-600">
                <Plus className="h-3 w-3" />
              </span>
              <span className="w-full truncate text-center text-[9.5px] font-medium leading-tight text-blue-600">
                Add tool
              </span>
            </button>
          </Tooltip>
        )}
      </div>
    </div>
  );
}

/** The show/hide control. Sits with the other map controls, by the header. */
export function ToolsToggle({
  open,
  onToggle,
  count,
}: {
  open: boolean;
  onToggle: () => void;
  count: number;
}) {
  return (
    <Tooltip content={open ? 'Hide the tool bar' : `Show the ${count} tools on this map`}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-label={open ? 'Hide tools' : 'Show tools'}
        className="flex items-center gap-1 rounded-md border border-blue-500 bg-white px-2 py-1 text-[11px] font-medium text-blue-700 shadow-sm transition hover:bg-blue-50"
      >
        {open ? <X className="h-3 w-3" /> : <Wrench className="h-3 w-3" />}
        {open ? 'Hide' : 'Tools'}
      </button>
    </Tooltip>
  );
}
